import type { StockCounts, StockSummary } from '../api/reports';
import { countCell } from './garmentMovement';

/**
 * Report B2, the stock summary — direction A of canvas "Stock summary" (owner, 2026-10-29): the
 * figures and a bar for where the garments are, then a register by category and design, each design
 * opening to a line per size and colour with the same columns. Every count and value is the
 * server's (`GET /api/report/stock-summary`); this words, groups and lays it out for the screen, the
 * paper and the spreadsheet.
 */

/** The workroom: at the cleaner, at the tailor, and back from a customer waiting to be checked. */
export function workroomOf(counts: StockCounts): number {
  return counts.atCleaner + counts.atTailor + counts.toCheck;
}

export interface StockColumn {
  key: string;
  /** The register's heading. */
  label: string;
  /** The same place said in full, over its figure. */
  figure: string;
  value: (counts: StockCounts) => number;
}

const always: StockColumn[] = [
  { key: 'ready', label: 'Ready', figure: 'Ready in the shop', value: (c) => c.ready },
  { key: 'booked', label: 'Booked', figure: 'Booked or held', value: (c) => c.booked },
  {
    key: 'withCustomers',
    label: 'With customers',
    figure: 'With customers',
    value: (c) => c.withCustomers,
  },
  { key: 'workroom', label: 'Workroom', figure: 'In the workroom', value: workroomOf },
  { key: 'damaged', label: 'Damaged', figure: 'Damaged', value: (c) => c.damaged },
];

const moving: StockColumn = {
  key: 'moving',
  label: 'Between branches',
  figure: 'Between branches',
  value: (c) => c.moving,
};

/**
 * The register's count columns. "Between branches" only when something is on the way — a shop with
 * one branch never sees it — so the columns always add up to the total.
 */
export function stockColumns(summary: Pick<StockSummary, 'counts'>): StockColumn[] {
  return summary.counts.moving > 0 ? [...always, moving] : always;
}

/** A count in a register cell: a dash for none, as the garments in and out report shows it. */
export const stockCell = countCell;

/**
 * A line's value, or null where there is none to show: the read carries no cost, or not one of the
 * line's garments has a purchase cost — a sum of nothing reads as "cost nothing", which is wrong.
 */
export function costShown(line: {
  atCost: number | null;
  total: number;
  withoutCost: number;
}): number | null {
  return line.atCost === null || line.withoutCost >= line.total ? null : line.atCost;
}

const garments = (count: number) => `${count} garment${count === 1 ? '' : 's'}`;

/**
 * The band's sentence: what the branch holds, what it cost when the read carries it, and how much
 * is free to rent today.
 */
export function stockSentence(
  summary: StockSummary & { scope?: string | null },
  branchName: string,
  money: (value: number) => string,
): string {
  const who = branchName || 'The branch';
  const scope = summary.scope ?? null;

  if (summary.total === 0) {
    return scope === null ? `${who} holds no garments yet.` : `${scope}: no garments.`;
  }

  const cost = summary.atCost === null ? '' : `, ${money(summary.atCost)} at cost`;
  const ready =
    summary.counts.ready === summary.total
      ? 'All of them are in the shop and free to rent today.'
      : `${summary.counts.ready} ${summary.counts.ready === 1 ? 'is' : 'are'} in the shop and free to rent today.`;

  return scope === null
    ? `${who} holds ${garments(summary.total)}${cost}. ${ready}`
    : `${scope}: ${garments(summary.total)}${cost}. ${ready}`;
}

export interface StockFigure {
  key: string;
  label: string;
  value: number;
  /** What a figure is made of, where it is more than one thing. */
  detail: string | null;
}

/** The figures over the register, in the columns' order. */
export function stockFigures(summary: StockSummary): StockFigure[] {
  const { counts } = summary;
  const parts = [
    counts.atCleaner > 0 ? `${counts.atCleaner} cleaner` : null,
    counts.atTailor > 0 ? `${counts.atTailor} tailor` : null,
    counts.toCheck > 0 ? `${counts.toCheck} to check` : null,
  ].filter((part): part is string => part !== null);

  return stockColumns(summary).map((column) => ({
    key: column.key,
    label: column.figure,
    value: column.value(counts),
    detail: column.key === 'workroom' && parts.length > 0 ? parts.join(' · ') : null,
  }));
}

/** The bar under the figures: each place's share of the garments, empty places left out. */
export function stockShares(
  summary: StockSummary,
): { key: string; label: string; value: number; share: number }[] {
  if (summary.total === 0) {
    return [];
  }

  return stockColumns(summary)
    .map((column) => ({
      key: column.key,
      label: column.label,
      value: column.value(summary.counts),
      share: column.value(summary.counts) / summary.total,
    }))
    .filter((part) => part.value > 0);
}

/**
 * The line under the bar: garments gone from stock, which the figures leave out, and garments with
 * no purchase cost, which the value leaves out. Null when there is neither.
 */
export function stockFootnote(summary: StockSummary): string | null {
  const gone = [
    summary.lost > 0 ? `${summary.lost} lost` : null,
    summary.retired > 0 ? `${summary.retired} retired` : null,
    summary.sold > 0 ? `${summary.sold} sold` : null,
  ].filter((part): part is string => part !== null);

  const sentences = [
    gone.length > 0 ? `Gone from stock, not counted above: ${gone.join(', ')}.` : null,
    summary.atCost !== null && summary.withoutCost > 0
      ? `${garments(summary.withoutCost)} ${summary.withoutCost === 1 ? 'has' : 'have'} no purchase cost and ${summary.withoutCost === 1 ? 'is' : 'are'} left out of the value.`
      : null,
  ].filter((sentence): sentence is string => sentence !== null);

  return sentences.length > 0 ? sentences.join(' ') : null;
}

/** The categories the picker offers, with how many garments each holds. */
export function categoryChoices(summary: StockSummary): { id: number; label: string }[] {
  return summary.categories.map((category) => ({
    id: category.categoryId,
    label: `${category.name || 'No category'} · ${category.total}`,
  }));
}

/**
 * The whole report as one category sees it — its figures, value and register — so the band, the
 * bar, the paper and the sheet all follow the picker. Garments gone from stock are counted for the
 * branch, not by category, so a category's view leaves them out rather than misplace them.
 */
export function inScope(
  summary: StockSummary,
  categoryId: number | null,
): StockSummary & { scope: string | null } {
  const category =
    categoryId === null
      ? undefined
      : summary.categories.find((candidate) => candidate.categoryId === categoryId);

  if (category === undefined) {
    return { ...summary, scope: null };
  }

  return {
    ...summary,
    scope: category.name || 'No category',
    counts: category.counts,
    total: category.total,
    atCost: category.atCost,
    withoutCost: category.designs.reduce((sum, design) => sum + design.withoutCost, 0),
    lost: 0,
    retired: 0,
    sold: 0,
    categories: [category],
  };
}

/**
 * The register as a sheet: a row per size and colour, with its category and design written out on
 * every row so the sheet filters by either, and the totals last.
 */
export function stockSheet(summary: StockSummary): { headers: string[]; rows: string[][] } {
  const columns = stockColumns(summary);
  const withCost = summary.atCost !== null;
  const headers = [
    'Category',
    'Design',
    'Code',
    'Size and colour',
    ...columns.map((column) => column.label),
    'Total',
    ...(withCost ? ['At cost'] : []),
  ];

  const rows = summary.categories.flatMap((category) =>
    category.designs.flatMap((design) =>
      design.variants.map((variant) => [
        category.name,
        design.name,
        design.code,
        variant.label,
        ...columns.map((column) => String(column.value(variant.counts))),
        String(variant.total),
        ...(withCost ? [String(variant.atCost ?? 0)] : []),
      ]),
    ),
  );

  rows.push([
    'All',
    '',
    '',
    '',
    ...columns.map((column) => String(column.value(summary.counts))),
    String(summary.total),
    ...(withCost ? [String(summary.atCost ?? 0)] : []),
  ]);

  return { headers, rows };
}
